import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { 
  Brain, Users, ShieldCheck, Lightning, ChartBar, Warning, 
  Plus, Trash, ArrowRight, SpinnerGap, CheckCircle, XCircle,
  TrendUp, TrendDown, Sparkle, Target, TreeStructure,
  CaretRight, Eye, MagicWand, Scales, Key, MagnifyingGlass
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import InfoTooltip from '../components/InfoTooltip';

export default function RoleMiningPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [suggestedRoles, setSuggestedRoles] = useState([]);
  const [overProvisioned, setOverProvisioned] = useState([]);
  const [roleOptimization, setRoleOptimization] = useState([]);
  const [entitlementPopularity, setEntitlementPopularity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  
  // Dialogs
  const [showRemediateDialog, setShowRemediateDialog] = useState(false);
  const [showEntitlementDetails, setShowEntitlementDetails] = useState(false);
  
  // Selected items
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedEntitlements, setSelectedEntitlements] = useState([]);
  
  // Form
  const [remediateReason, setRemediateReason] = useState('Over-provisioning remediation per role mining analysis');

  // AI states
  const [aiInsights, setAiInsights] = useState(null);
  const [aiInsightsLoading, setAiInsightsLoading] = useState(false);
  const [aiExplaining, setAiExplaining] = useState(null); // ID of suggestion being explained
  const [aiExplanations, setAiExplanations] = useState({}); // {suggestionId: explanation}
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatSessionId] = useState(`role-mining-chat-${Date.now()}`);

  // Compare roles state
  const [allRoles, setAllRoles] = useState([]);
  const [compareSelection, setCompareSelection] = useState([]);
  const [compareResult, setCompareResult] = useState(null);
  const [compareLoading, setCompareLoading] = useState(false);
  const [compareSearch, setCompareSearch] = useState('');

  const loadAllRoles = useCallback(async () => {
    try {
      const res = await API.get('/roles', { params: { limit: 200 } });
      setAllRoles(res.data.items || []);
    } catch (err) {
      void 0;
    }
  }, []);

  const toggleCompareRole = (role) => {
    setCompareSelection(prev => {
      const exists = prev.find(r => r.id === role.id);
      if (exists) return prev.filter(r => r.id !== role.id);
      if (prev.length >= 3) { toast.error('Maximum 3 roles can be compared'); return prev; }
      return [...prev, role];
    });
    setCompareResult(null);
  };

  const runComparison = async () => {
    if (compareSelection.length < 2) { toast.error('Select at least 2 roles'); return; }
    setCompareLoading(true);
    try {
      const res = await API.post('/roles/compare', { role_ids: compareSelection.map(r => r.id) });
      setCompareResult(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Comparison failed');
    } finally {
      setCompareLoading(false);
    }
  };
  const loadStats = useCallback(async () => {
    try {
      const res = await API.get('/role-mining/stats');
      setStats(res.data);
    } catch (err) {
      toast.error('Failed to load statistics');
    }
  }, []);

  const loadSuggestedRoles = useCallback(async () => {
    try {
      setAnalyzing(true);
      const res = await API.get('/role-mining/suggested-roles', { params: { min_users: 2, limit: 10 } });
      setSuggestedRoles(res.data.suggestions);
    } catch (err) {
      toast.error('Failed to analyze role patterns');
    } finally {
      setAnalyzing(false);
    }
  }, []);

  const loadOverProvisioned = useCallback(async () => {
    try {
      const res = await API.get('/role-mining/over-provisioned', { params: { threshold_multiplier: 2.0, limit: 20 } });
      setOverProvisioned(res.data.users);
    } catch (err) {
      toast.error('Failed to load over-provisioned users');
    }
  }, []);

  const loadRoleOptimization = useCallback(async () => {
    try {
      const res = await API.get('/role-mining/role-optimization');
      setRoleOptimization(res.data.suggestions);
    } catch (err) {
      toast.error('Failed to load optimization suggestions');
    }
  }, []);

  const loadEntitlementPopularity = useCallback(async () => {
    try {
      const res = await API.get('/role-mining/entitlement-popularity', { params: { limit: 15 } });
      setEntitlementPopularity(res.data);
    } catch (err) {
      toast.error('Failed to load entitlement data');
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([
        loadStats(),
        loadSuggestedRoles(),
        loadOverProvisioned(),
        loadRoleOptimization(),
        loadEntitlementPopularity()
      ]);
      setLoading(false);
    };
    init();
  }, [loadStats, loadSuggestedRoles, loadOverProvisioned, loadRoleOptimization, loadEntitlementPopularity]);

  const handleRemediate = async () => {
    if (!selectedUser || selectedEntitlements.length === 0) return;
    
    try {
      const res = await API.post('/role-mining/remediate', {
        identity_id: selectedUser.identity_id,
        entitlement_ids: selectedEntitlements,
        reason: remediateReason
      });
      toast.success(res.data.message);
      setShowRemediateDialog(false);
      setSelectedUser(null);
      setSelectedEntitlements([]);
      loadOverProvisioned();
      loadStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remediate access');
    }
  };

  const openCreateRoleDialog = (suggestion) => {
    // Navigate to the Roles page wizard with suggestion data pre-filled
    // Use entitlement_ids (not names) so the role stores proper IDs
    navigate('/roles', {
      state: {
        fromMining: true,
        suggestion: {
          name: suggestion.suggested_name,
          description: suggestion.suggested_description,
          entitlements: suggestion.entitlement_ids || [],
          entitlement_names: suggestion.entitlements || [],
          member_identity_ids: suggestion.all_user_ids || suggestion.sample_user_ids || [],
          user_count: suggestion.user_count
        }
      }
    });
  };

  const openRemediateDialog = (user) => {
    setSelectedUser(user);
    setSelectedEntitlements(user.entitlements.map(e => e.id));
    setShowRemediateDialog(true);
  };

  // AI Handlers
  const generateAiInsights = async () => {
    setAiInsightsLoading(true);
    try {
      const res = await API.post('/role-mining/ai-insights');
      setAiInsights(res.data);
      toast.success('AI insights generated');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to generate insights'); }
    finally { setAiInsightsLoading(false); }
  };

  const explainSuggestion = async (suggestion) => {
    setAiExplaining(suggestion.id);
    try {
      const res = await API.post('/role-mining/ai-explain-suggestion', {
        entitlements: suggestion.entitlements,
        user_count: suggestion.user_count
      });
      setAiExplanations(prev => ({ ...prev, [suggestion.id]: res.data }));
    } catch (err) { toast.error('Failed to get AI explanation'); }
    finally { setAiExplaining(null); }
  };

  const sendChatMessage = async () => {
    if (!chatInput.trim()) return;
    const question = chatInput.trim();
    setChatMessages(prev => [...prev, { role: 'user', text: question }]);
    setChatInput('');
    setChatLoading(true);
    try {
      const res = await API.post('/role-mining/ai-chat', { question, session_id: chatSessionId });
      setChatMessages(prev => [...prev, { role: 'assistant', text: res.data.answer }]);
    } catch (err) {
      setChatMessages(prev => [...prev, { role: 'assistant', text: 'Sorry, I encountered an error. Please try again.' }]);
    }
    finally { setChatLoading(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <SpinnerGap size={32} className="animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-navy-900 flex items-center gap-2">
            <Brain size={28} weight="duotone" className="text-purple-600" />
            Role Mining & Optimization <InfoTooltip concept="role_mining" />
          </h1>
          <p className="text-sm text-navy-500 mt-1">
            AI-powered analysis of access patterns to optimize your role structure
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={generateAiInsights}
            disabled={aiInsightsLoading}
            className="bg-purple-600 hover:bg-purple-700 text-white rounded-sm"
            data-testid="generate-ai-insights-btn"
          >
            {aiInsightsLoading ? <SpinnerGap size={16} className="mr-2 animate-spin" /> : <Sparkle size={16} className="mr-2" />}
            {aiInsightsLoading ? 'Analyzing...' : 'AI Insights'}
          </Button>
          <Button 
            onClick={() => {
              setLoading(true);
              Promise.all([loadStats(), loadSuggestedRoles(), loadOverProvisioned(), loadRoleOptimization(), loadEntitlementPopularity()])
                .finally(() => setLoading(false));
            }}
            variant="outline"
            className="rounded-sm"
          >
            <Lightning size={16} className="mr-2" />
            Re-analyze
          </Button>
        </div>
      </div>

      {/* Stats Overview */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <StatCard icon={Users} label="Identities" value={stats.total_identities} />
          <StatCard icon={ShieldCheck} label="Entitlements" value={stats.total_entitlements} />
          <StatCard icon={TreeStructure} label="Roles" value={stats.total_roles} />
          <StatCard icon={ChartBar} label="Avg per User" value={stats.avg_entitlements_per_user} />
          <StatCard 
            icon={Target} 
            label="Role Coverage" 
            value={`${stats.role_coverage_percent}%`} 
            accent={stats.role_coverage_percent < 50 ? 'orange' : 'green'}
          />
          <StatCard 
            icon={Warning} 
            label="Over-Provisioned" 
            value={stats.over_provisioned_count} 
            accent={stats.over_provisioned_count > 0 ? 'red' : 'green'}
          />
        </div>
      )}

      {/* AI Insights Panel */}
      {aiInsights && (
        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-sm p-5 space-y-3" data-testid="ai-insights-panel">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-purple-900 flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
              <Sparkle size={16} weight="duotone" className="text-purple-600" /> AI Access Governance Insights
            </h3>
            <Button variant="ghost" size="sm" onClick={() => setAiInsights(null)} className="text-purple-400 hover:text-purple-600 h-6 px-2 text-xs">Dismiss</Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {(aiInsights.insights || []).map((insight, idx) => (
              <div key={`insight-${insight.category}-${idx}`} className={`bg-white rounded-sm p-3.5 border ${insight.severity === 'critical' ? 'border-red-300' : insight.severity === 'high' ? 'border-amber-300' : insight.severity === 'medium' ? 'border-blue-200' : 'border-slate-200'}`} data-testid={`ai-insight-${idx}`}>
                <div className="flex items-center gap-2 mb-1.5">
                  <Badge className={`text-[9px] ${insight.severity === 'critical' ? 'bg-red-100 text-red-700' : insight.severity === 'high' ? 'bg-amber-100 text-amber-700' : insight.severity === 'medium' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-navy-600'}`}>
                    {insight.severity}
                  </Badge>
                  <Badge className="text-[9px] bg-purple-100 text-purple-700">{insight.category}</Badge>
                </div>
                <p className="text-sm font-semibold text-navy-900">{insight.title}</p>
                <p className="text-xs text-navy-600 mt-1">{insight.insight}</p>
                <p className="text-xs text-purple-700 mt-2 font-medium">{insight.action}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Main Content Tabs */}
      <Tabs defaultValue="suggestions" className="space-y-4">
        <TabsList className="bg-slate-100 rounded-lg p-1">
          <TabsTrigger value="suggestions" className="rounded-md data-[state=active]:bg-white">
            <Sparkle size={16} className="mr-2 text-purple-500" />
            Suggested Roles
          </TabsTrigger>
          <TabsTrigger value="over-provisioned" className="rounded-md data-[state=active]:bg-white">
            <Warning size={16} className="mr-2 text-red-500" />
            Over-Provisioned ({overProvisioned.length})
          </TabsTrigger>
          <TabsTrigger value="optimization" className="rounded-md data-[state=active]:bg-white">
            <MagicWand size={16} className="mr-2 text-blue-500" />
            Role Optimization
          </TabsTrigger>
          <TabsTrigger value="popularity" className="rounded-md data-[state=active]:bg-white">
            <TrendUp size={16} className="mr-2 text-emerald-500" />
            Entitlement Usage
          </TabsTrigger>
          <TabsTrigger value="ai-chat" className="rounded-md data-[state=active]:bg-white">
            <Brain size={16} className="mr-2 text-purple-500" />
            AI Assistant
          </TabsTrigger>
          <TabsTrigger value="compare" className="rounded-md data-[state=active]:bg-white" data-testid="compare-tab" onClick={() => { if (allRoles.length === 0) loadAllRoles(); }}>
            <Scales size={16} className="mr-2 text-indigo-500" />
            Compare Roles
          </TabsTrigger>
        </TabsList>

        {/* Suggested Roles Tab */}
        <TabsContent value="suggestions" className="space-y-4">
          {analyzing ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <SpinnerGap size={40} className="animate-spin text-purple-500 mb-4" />
                <p className="text-navy-600 font-medium">Analyzing access patterns with AI...</p>
                <p className="text-sm text-navy-400">This may take a moment</p>
              </CardContent>
            </Card>
          ) : suggestedRoles.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <CheckCircle size={40} className="text-emerald-500 mb-4" />
                <p className="text-navy-600 font-medium">No role suggestions at this time</p>
                <p className="text-sm text-navy-400">Your access patterns are well-organized</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {suggestedRoles.map((suggestion, idx) => (
                <Card key={suggestion.id} className="hover:shadow-md transition-shadow">
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                          <Sparkle size={20} weight="duotone" className="text-purple-600" />
                        </div>
                        <div>
                          <CardTitle className="text-base">{suggestion.suggested_name}</CardTitle>
                          <CardDescription className="text-xs mt-0.5">
                            {suggestion.suggested_description}
                          </CardDescription>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-purple-100 text-purple-700">
                          {suggestion.confidence_score}% confidence
                        </Badge>
                        {suggestion.similar_role_exists && (
                          <Badge className="bg-yellow-100 text-yellow-700">
                            Similar to: {suggestion.similar_role_name}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-6 text-sm">
                        <div className="flex items-center gap-1.5">
                          <ShieldCheck size={14} className="text-navy-400" />
                          <span className="text-navy-600">{suggestion.entitlement_count} entitlements</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Users size={14} className="text-navy-400" />
                          <span className="text-navy-600">{suggestion.user_count} users with this pattern</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => explainSuggestion(suggestion)}
                          disabled={aiExplaining === suggestion.id}
                          className="text-purple-600 hover:text-purple-700"
                          data-testid={`explain-btn-${suggestion.id}`}
                        >
                          {aiExplaining === suggestion.id ? <SpinnerGap size={14} className="mr-1 animate-spin" /> : <Brain size={14} className="mr-1" />}
                          {aiExplaining === suggestion.id ? 'Analyzing...' : 'AI Explain'}
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => {
                            setSelectedSuggestion(suggestion);
                            setShowEntitlementDetails(true);
                          }}
                        >
                          <Eye size={14} className="mr-1" /> View Details
                        </Button>
                        <Button 
                          size="sm" 
                          onClick={() => openCreateRoleDialog(suggestion)}
                          disabled={suggestion.similar_role_exists}
                          className="bg-purple-600 hover:bg-purple-700"
                        >
                          <Plus size={14} className="mr-1" /> Create Role
                        </Button>
                      </div>
                    </div>
                    {/* Sample Users Preview */}
                    {suggestion.sample_users_detail && suggestion.sample_users_detail.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-slate-100">
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-navy-400 mb-2">Sample Members</p>
                        <div className="flex flex-wrap gap-2">
                          {suggestion.sample_users_detail.map(u => (
                            <div key={u.id} className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-sm px-2 py-1" data-testid={`sample-user-${u.id}`}>
                              <div className="w-5 h-5 rounded-full bg-navy-100 flex items-center justify-center text-[9px] font-bold text-navy-600">
                                {(u.display_name || '?')[0]}
                              </div>
                              <span className="text-xs text-navy-700">{u.display_name}</span>
                              {u.department && <span className="text-[10px] text-navy-400">({u.department})</span>}
                            </div>
                          ))}
                          {suggestion.user_count > suggestion.sample_users_detail.length && (
                            <div className="flex items-center gap-1 px-2 py-1 text-xs text-navy-400">
                              +{suggestion.user_count - suggestion.sample_users_detail.length} more
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    {/* AI Explanation */}
                    {aiExplanations[suggestion.id] && (
                      <div className="mt-3 pt-3 border-t border-purple-100 bg-purple-50/50 -mx-6 -mb-4 px-6 pb-4 rounded-b-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <Brain size={14} className="text-purple-600" />
                          <span className="text-xs font-bold text-purple-800 uppercase tracking-wider">AI Analysis</span>
                          <Badge className={`text-[9px] ${aiExplanations[suggestion.id].risk_assessment === 'high' ? 'bg-red-100 text-red-700' : aiExplanations[suggestion.id].risk_assessment === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                            {aiExplanations[suggestion.id].risk_assessment} risk
                          </Badge>
                        </div>
                        <p className="text-xs text-navy-700"><strong>Purpose:</strong> {aiExplanations[suggestion.id].role_purpose}</p>
                        <p className="text-xs text-navy-700 mt-1"><strong>Risk:</strong> {aiExplanations[suggestion.id].risk_explanation}</p>
                        <p className="text-xs text-purple-700 mt-1 font-medium">{aiExplanations[suggestion.id].recommendation}</p>
                        {aiExplanations[suggestion.id].suggested_owner && (
                          <p className="text-xs text-navy-500 mt-1">Suggested Owner: {aiExplanations[suggestion.id].suggested_owner}</p>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Over-Provisioned Tab */}
        <TabsContent value="over-provisioned" className="space-y-4">
          {overProvisioned.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <CheckCircle size={40} className="text-emerald-500 mb-4" />
                <p className="text-navy-600 font-medium">No over-provisioned users detected</p>
                <p className="text-sm text-navy-400">All users have appropriate access levels</p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Warning size={18} className="text-red-500" />
                  Users with Excessive Access
                </CardTitle>
                <CardDescription>
                  Users with more than 2x the average number of entitlements
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="text-[10px] font-bold uppercase">User</TableHead>
                      <TableHead className="text-[10px] font-bold uppercase">Department</TableHead>
                      <TableHead className="text-[10px] font-bold uppercase text-center">Entitlements</TableHead>
                      <TableHead className="text-[10px] font-bold uppercase text-center">Excess</TableHead>
                      <TableHead className="text-[10px] font-bold uppercase text-center">Risk</TableHead>
                      <TableHead className="text-[10px] font-bold uppercase text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {overProvisioned.map((user) => (
                      <TableRow key={user.identity_id} className="hover:bg-red-50/50">
                        <TableCell>
                          <div>
                            <p className="font-medium text-sm">{user.identity_name}</p>
                            <p className="text-xs text-navy-400">{user.email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-navy-600">{user.department || '-'}</span>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className="font-bold text-lg text-navy-900">{user.entitlement_count}</span>
                          <span className="text-xs text-navy-400 ml-1">/ {user.avg_entitlements} avg</span>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge className="bg-red-100 text-red-700">
                            +{user.excess_count}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <RiskBadge level={user.risk_level} score={user.risk_score} />
                        </TableCell>
                        <TableCell className="text-right">
                          <Button 
                            size="sm" 
                            variant="destructive"
                            onClick={() => openRemediateDialog(user)}
                            className="h-7 text-xs"
                          >
                            <Trash size={12} className="mr-1" /> Remediate
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Role Optimization Tab */}
        <TabsContent value="optimization" className="space-y-4">
          {roleOptimization.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <CheckCircle size={40} className="text-emerald-500 mb-4" />
                <p className="text-navy-600 font-medium">Your role structure is optimized!</p>
                <p className="text-sm text-navy-400">No optimization suggestions at this time</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {roleOptimization.map((suggestion, idx) => (
                <Card key={suggestion.id || `sug-${idx}`} className={`border-l-4 ${
                  suggestion.severity === 'high' ? 'border-l-red-500' :
                  suggestion.severity === 'medium' ? 'border-l-yellow-500' : 'border-l-blue-500'
                }`}>
                  <CardContent className="py-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                          suggestion.type === 'merge' ? 'bg-yellow-100' :
                          suggestion.type === 'unused' ? 'bg-blue-100' : 'bg-red-100'
                        }`}>
                          {suggestion.type === 'merge' ? <TreeStructure size={16} className="text-yellow-600" /> :
                           suggestion.type === 'unused' ? <XCircle size={16} className="text-blue-600" /> :
                           <Warning size={16} className="text-red-600" />}
                        </div>
                        <div>
                          <p className="font-medium text-sm text-navy-900">{suggestion.title}</p>
                          <p className="text-xs text-navy-500 mt-0.5">{suggestion.description}</p>
                          <p className="text-xs text-emerald-600 mt-1 font-medium">
                            Potential benefit: {suggestion.potential_savings}
                          </p>
                        </div>
                      </div>
                      <Badge className={
                        suggestion.severity === 'high' ? 'bg-red-100 text-red-700' :
                        suggestion.severity === 'medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'
                      }>
                        {suggestion.severity}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Entitlement Popularity Tab */}
        <TabsContent value="popularity" className="space-y-4">
          {entitlementPopularity && (
            <div className="grid md:grid-cols-2 gap-4">
              {/* Most Popular */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <TrendUp size={18} className="text-emerald-500" />
                    Most Used Entitlements
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {entitlementPopularity.most_popular.slice(0, 10).map((ent, idx) => (
                    <div key={ent.id} className="flex items-center justify-between py-1.5 border-b border-slate-100 last:border-0">
                      <div className="flex items-center gap-2">
                        <span className="w-5 h-5 rounded bg-emerald-100 text-emerald-700 text-xs flex items-center justify-center font-bold">
                          {idx + 1}
                        </span>
                        <span className="text-sm text-navy-700 truncate max-w-[200px]">{ent.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">{ent.source}</Badge>
                        <span className="text-sm font-bold text-navy-900">{ent.assignment_count}</span>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Unused */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <TrendDown size={18} className="text-red-500" />
                    Unused Entitlements ({entitlementPopularity.unused_count})
                  </CardTitle>
                  <CardDescription>Consider removing these to reduce complexity</CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                  {entitlementPopularity.unused.slice(0, 10).map((ent) => (
                    <div key={ent.id} className="flex items-center justify-between py-1.5 border-b border-slate-100 last:border-0">
                      <span className="text-sm text-navy-500 truncate max-w-[200px]">{ent.name}</span>
                      <Badge variant="outline" className="text-xs">{ent.source}</Badge>
                    </div>
                  ))}
                  {entitlementPopularity.unused_count > 10 && (
                    <p className="text-xs text-navy-400 text-center pt-2">
                      +{entitlementPopularity.unused_count - 10} more unused entitlements
                    </p>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* AI Assistant Chat Tab */}
        <TabsContent value="ai-chat" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Brain size={18} weight="duotone" className="text-purple-600" />
                AI Access Governance Assistant
              </CardTitle>
              <CardDescription>
                Ask questions about your access data, roles, compliance posture, and get recommendations.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="border border-slate-200 rounded-sm bg-slate-50 h-[400px] flex flex-col">
                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="ai-chat-messages">
                  {chatMessages.length === 0 && (
                    <div className="text-center py-12">
                      <Brain size={40} className="mx-auto text-purple-300 mb-3" />
                      <p className="text-sm text-navy-500 font-medium">Ask me anything about your access governance</p>
                      <div className="flex flex-wrap gap-2 justify-center mt-4">
                        {[
                          "What are the biggest access risks right now?",
                          "Which roles have the most entitlements?",
                          "How many orphaned identities do we have?",
                          "What's our compliance posture?",
                          "Suggest ways to reduce over-provisioning"
                        ].map((q) => (
                          <button key={q} onClick={() => { setChatInput(q); }} className="text-xs bg-white border border-purple-200 text-purple-700 px-3 py-1.5 rounded-sm hover:bg-purple-50 transition-colors">
                            {q}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {chatMessages.map((msg, idx) => (
                    <div key={`msg-${idx}`} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] rounded-sm px-4 py-2.5 ${msg.role === 'user' ? 'bg-orange-500 text-white' : 'bg-white border border-slate-200 text-navy-700'}`} data-testid={`chat-msg-${idx}`}>
                        <p className="text-sm whitespace-pre-wrap">{msg.text}</p>
                      </div>
                    </div>
                  ))}
                  {chatLoading && (
                    <div className="flex justify-start">
                      <div className="bg-white border border-slate-200 rounded-sm px-4 py-2.5 flex items-center gap-2">
                        <SpinnerGap size={14} className="animate-spin text-purple-500" />
                        <span className="text-sm text-navy-500">Thinking...</span>
                      </div>
                    </div>
                  )}
                </div>
                {/* Input */}
                <div className="border-t border-slate-200 p-3 bg-white rounded-b-sm">
                  <form onSubmit={(e) => { e.preventDefault(); sendChatMessage(); }} className="flex gap-2">
                    <Input
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      placeholder="Ask about access patterns, risks, compliance..."
                      className="flex-1 rounded-sm text-sm"
                      disabled={chatLoading}
                      data-testid="ai-chat-input"
                    />
                    <Button type="submit" disabled={chatLoading || !chatInput.trim()} className="bg-purple-600 hover:bg-purple-700 text-white rounded-sm" data-testid="ai-chat-send">
                      <ArrowRight size={16} />
                    </Button>
                  </form>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Compare Roles Tab */}
        <TabsContent value="compare" className="space-y-4">
          <div className="grid xl:grid-cols-12 gap-4">
            {/* Role Selector */}
            <div className="xl:col-span-4 bg-white border border-slate-200 rounded-lg" data-testid="compare-role-selector">
              <div className="px-4 py-3 border-b border-slate-200">
                <h3 className="text-sm font-bold text-navy-900 uppercase tracking-wider">Select Roles (2-3)</h3>
                <div className="relative mt-2">
                  <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-navy-400" />
                  <input
                    value={compareSearch}
                    onChange={(e) => setCompareSearch(e.target.value)}
                    placeholder="Search roles..."
                    className="w-full pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                    data-testid="compare-search"
                  />
                </div>
              </div>
              <div className="max-h-[400px] overflow-y-auto divide-y divide-slate-100">
                {allRoles
                  .filter(r => compareSearch === '' || r.name?.toLowerCase().includes(compareSearch.toLowerCase()))
                  .map(role => {
                    const selected = compareSelection.find(r => r.id === role.id);
                    return (
                      <button
                        key={role.id}
                        data-testid={`compare-role-${role.id}`}
                        onClick={() => toggleCompareRole(role)}
                        className={`w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors flex items-center gap-3 ${selected ? 'bg-indigo-50 border-l-2 border-indigo-500' : ''}`}
                      >
                        <ShieldCheck size={16} className={selected ? 'text-indigo-600' : 'text-navy-400'} />
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium truncate ${selected ? 'text-indigo-800' : 'text-navy-900'}`}>{role.name}</p>
                          <p className="text-xs text-navy-400">{(role.entitlements || []).length} entitlements</p>
                        </div>
                        {selected && <CheckCircle size={16} weight="fill" className="text-indigo-500 shrink-0" />}
                      </button>
                    );
                  })}
                {allRoles.length === 0 && (
                  <div className="px-4 py-8 text-center text-sm text-navy-400">Loading roles...</div>
                )}
              </div>
              <div className="px-4 py-3 border-t border-slate-200">
                <Button
                  data-testid="compare-run-btn"
                  onClick={runComparison}
                  disabled={compareSelection.length < 2 || compareLoading}
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded-sm"
                >
                  {compareLoading ? <SpinnerGap size={14} className="mr-1.5 animate-spin" /> : <Scales size={14} className="mr-1.5" />}
                  Compare {compareSelection.length} Roles
                </Button>
              </div>
            </div>

            {/* Comparison Results */}
            <div className="xl:col-span-8">
              {!compareResult ? (
                <div className="bg-white border border-slate-200 rounded-lg p-12 text-center" data-testid="compare-empty">
                  <Scales size={40} className="text-navy-300 mx-auto mb-3" />
                  <h3 className="text-base font-semibold text-navy-700">Select Roles to Compare</h3>
                  <p className="text-sm text-navy-400 mt-1 max-w-sm mx-auto">Pick 2-3 roles from the list to see which entitlements overlap and which are unique to each role.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-3 gap-3" data-testid="compare-summary">
                    {compareResult.roles.map((role, idx) => {
                      const colors = ['bg-indigo-50 border-indigo-200', 'bg-emerald-50 border-emerald-200', 'bg-amber-50 border-amber-200'];
                      const textColors = ['text-indigo-800', 'text-emerald-800', 'text-amber-800'];
                      return (
                        <div key={role.id} className={`border rounded-lg p-4 ${colors[idx]}`} data-testid={`compare-role-summary-${idx}`}>
                          <p className={`text-sm font-bold ${textColors[idx]} truncate`}>{role.name}</p>
                          <p className="text-xs text-navy-500 mt-0.5 truncate">{role.description || 'No description'}</p>
                          <div className="flex items-center gap-3 mt-3">
                            <div>
                              <p className="text-lg font-bold text-navy-900">{role.total_entitlements}</p>
                              <p className="text-[9px] text-navy-500 uppercase">Total</p>
                            </div>
                            <div>
                              <p className="text-lg font-bold text-emerald-600">{role.shared_count}</p>
                              <p className="text-[9px] text-navy-500 uppercase">Shared</p>
                            </div>
                            <div>
                              <p className="text-lg font-bold text-orange-600">{role.unique_count}</p>
                              <p className="text-[9px] text-navy-500 uppercase">Unique</p>
                            </div>
                          </div>
                          <div className="flex gap-1 mt-2">
                            {role.requestable && <Badge className="text-[9px] bg-emerald-100 text-emerald-700">Requestable</Badge>}
                            {role.privileged && <Badge className="text-[9px] bg-amber-100 text-amber-700">Privileged</Badge>}
                            <Badge className="text-[9px] bg-slate-100 text-navy-600">{role.assignment_count} members</Badge>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Overlap Summary */}
                  <div className="bg-white border border-slate-200 rounded-lg px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-6 text-sm">
                      <span className="text-navy-500">
                        <span className="font-bold text-navy-900">{compareResult.summary.total_unique_entitlements}</span> unique entitlements
                      </span>
                      <span className="text-navy-500">
                        <span className="font-bold text-emerald-600">{compareResult.summary.shared_count}</span> shared across all
                      </span>
                      <span className="text-navy-500">
                        <span className="font-bold text-indigo-600">{compareResult.summary.overlap_percentage}%</span> overlap
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <Badge className="text-[10px] bg-emerald-100 text-emerald-700">Shared</Badge>
                      <Badge className="text-[10px] bg-blue-100 text-blue-700">Partial</Badge>
                      <Badge className="text-[10px] bg-slate-100 text-navy-600">Unique</Badge>
                    </div>
                  </div>

                  {/* Entitlement Matrix */}
                  <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="compare-matrix">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                          <th className="text-left px-4 py-2.5 text-xs font-semibold text-navy-500 uppercase">Entitlement</th>
                          <th className="text-left px-4 py-2.5 text-xs font-semibold text-navy-500 uppercase">Type</th>
                          {compareResult.roles.map((role, idx) => (
                            <th key={role.id} className="text-center px-3 py-2.5 text-xs font-semibold text-navy-500 uppercase truncate max-w-[120px]">
                              {role.name}
                            </th>
                          ))}
                          <th className="text-center px-3 py-2.5 text-xs font-semibold text-navy-500 uppercase">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {compareResult.entitlements.length === 0 ? (
                          <tr><td colSpan={3 + compareResult.roles.length} className="text-center py-8 text-navy-400">No entitlements to compare</td></tr>
                        ) : (
                          compareResult.entitlements.map(ent => (
                            <tr key={ent.id} className="hover:bg-slate-50 transition-colors" data-testid={`compare-ent-${ent.id}`}>
                              <td className="px-4 py-2.5">
                                <div className="flex items-center gap-2">
                                  <Key size={12} className="text-navy-400 shrink-0" />
                                  <span className="font-medium text-navy-900 truncate max-w-[180px]">{ent.name}</span>
                                </div>
                              </td>
                              <td className="px-4 py-2.5 text-xs text-navy-500">{ent.type}</td>
                              {ent.membership.map((has, idx) => (
                                <td key={`${ent.name}-col-${idx}`} className="text-center px-3 py-2.5">
                                  {has ? (
                                    <CheckCircle size={18} weight="fill" className="mx-auto text-emerald-500" />
                                  ) : (
                                    <XCircle size={18} weight="regular" className="mx-auto text-slate-300" />
                                  )}
                                </td>
                              ))}
                              <td className="text-center px-3 py-2.5">
                                <Badge className={`text-[9px] ${
                                  ent.status === 'shared' ? 'bg-emerald-100 text-emerald-700' :
                                  ent.status === 'partial' ? 'bg-blue-100 text-blue-700' :
                                  'bg-slate-100 text-navy-600'
                                }`}>
                                  {ent.status}
                                </Badge>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Remediate Dialog */}
      <Dialog open={showRemediateDialog} onOpenChange={setShowRemediateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Warning size={20} />
              Remediate Over-Provisioned Access
            </DialogTitle>
            <DialogDescription>
              Revoke excessive entitlements from {selectedUser?.identity_name}
            </DialogDescription>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4 py-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-800">
                  <strong>{selectedUser.identity_name}</strong> has <strong>{selectedUser.entitlement_count}</strong> entitlements, 
                  which is <strong>{selectedUser.excess_count}</strong> more than the average ({selectedUser.avg_entitlements}).
                </p>
              </div>
              
              <div className="space-y-2">
                <Label>Reason for Remediation</Label>
                <Textarea 
                  value={remediateReason}
                  onChange={(e) => setRemediateReason(e.target.value)}
                  rows={2}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Select entitlements to revoke ({selectedEntitlements.length} selected)</Label>
                <div className="max-h-48 overflow-y-auto border rounded-lg p-2 bg-slate-50">
                  {selectedUser.entitlements.map((ent) => (
                    <label key={ent.id} className="flex items-center gap-2 py-1.5 hover:bg-slate-100 px-1 rounded cursor-pointer">
                      <input 
                        type="checkbox"
                        checked={selectedEntitlements.includes(ent.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedEntitlements([...selectedEntitlements, ent.id]);
                          } else {
                            setSelectedEntitlements(selectedEntitlements.filter(id => id !== ent.id));
                          }
                        }}
                        className="rounded border-slate-300"
                      />
                      <span className="text-sm text-navy-600">{ent.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRemediateDialog(false)}>Cancel</Button>
            <Button 
              variant="destructive" 
              onClick={handleRemediate}
              disabled={selectedEntitlements.length === 0}
            >
              <Trash size={14} className="mr-1" /> Revoke {selectedEntitlements.length} Entitlements
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Entitlement Details Dialog */}
      <Dialog open={showEntitlementDetails} onOpenChange={setShowEntitlementDetails}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{selectedSuggestion?.suggested_name}</DialogTitle>
            <DialogDescription>{selectedSuggestion?.suggested_description}</DialogDescription>
          </DialogHeader>
          {selectedSuggestion && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-4 text-sm">
                <Badge className="bg-purple-100 text-purple-700">
                  {selectedSuggestion.confidence_score}% confidence
                </Badge>
                <span className="text-navy-500">
                  {selectedSuggestion.user_count} users with this pattern
                </span>
              </div>
              <div className="space-y-2">
                <Label>Entitlements in this pattern ({selectedSuggestion.entitlement_count})</Label>
                <div className="max-h-40 overflow-y-auto border rounded-lg p-3 bg-slate-50 space-y-1">
                  {selectedSuggestion.entitlements.map((ent) => (
                    <div key={ent} className="flex items-center gap-2 py-1">
                      <ShieldCheck size={14} className="text-purple-500" />
                      <span className="text-sm text-navy-700">{ent}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label>Members ({selectedSuggestion.user_count})</Label>
                <div className="max-h-40 overflow-y-auto border rounded-lg p-3 bg-slate-50 space-y-1">
                  {(selectedSuggestion.sample_users_detail || []).map((u) => (
                    <div key={u.id} className="flex items-center gap-2 py-1">
                      <div className="w-5 h-5 rounded-full bg-navy-100 flex items-center justify-center text-[9px] font-bold text-navy-600 shrink-0">
                        {(u.display_name || '?')[0]}
                      </div>
                      <span className="text-sm text-navy-700">{u.display_name}</span>
                      {u.email && <span className="text-xs text-navy-400">{u.email}</span>}
                    </div>
                  ))}
                  {selectedSuggestion.user_count > (selectedSuggestion.sample_users_detail || []).length && (
                    <div className="text-xs text-navy-400 py-1 text-center">
                      +{selectedSuggestion.user_count - (selectedSuggestion.sample_users_detail || []).length} more users will be assigned when this role is created
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEntitlementDetails(false)}>Close</Button>
            <Button onClick={() => {
              setShowEntitlementDetails(false);
              openCreateRoleDialog(selectedSuggestion);
            }} className="bg-purple-600 hover:bg-purple-700">
              <Plus size={14} className="mr-1" /> Create This Role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent }) {
  const accentColors = {
    orange: 'bg-orange-50 border-orange-200 text-orange-600',
    green: 'bg-emerald-50 border-emerald-200 text-emerald-600',
    red: 'bg-red-50 border-red-200 text-red-600',
  };
  
  return (
    <div className={`rounded-lg border p-4 ${accent ? accentColors[accent] : 'bg-white border-slate-200'}`}>
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${accent ? 'bg-white/50' : 'bg-slate-100'}`}>
          <Icon size={18} weight="duotone" className={accent ? '' : 'text-navy-500'} />
        </div>
        <div>
          <p className={`text-2xl font-bold ${accent ? '' : 'text-navy-900'}`}>{value}</p>
          <p className="text-xs uppercase tracking-wider opacity-70">{label}</p>
        </div>
      </div>
    </div>
  );
}

function RiskBadge({ level, score }) {
  const colors = {
    critical: 'bg-red-100 text-red-700 border-red-200',
    high: 'bg-orange-100 text-orange-700 border-orange-200',
    medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  };
  
  return (
    <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full border text-xs font-medium ${colors[level] || colors.medium}`}>
      <div className="w-1.5 h-1.5 rounded-full bg-current" />
      {score}%
    </div>
  );
}
